import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "@/hooks/useEventTracking";
import { CopyButton } from "@/components/CopyButton";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, MapPin, Phone, Mail, Globe, AlertTriangle, Heart, Users, Dog, Cat, BadgeCheck, Clock, FileEdit, Info, ExternalLink } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Button } from "@/components/ui/button";
import { ShareChoiceDialog } from "@/components/ShareChoiceDialog";
import { useShelters, useShelterDetails } from "@/hooks/useShelters";
import { supabase } from "@/integrations/supabase/client";
import { extractIdFromSlug, matchesShortId, buildShelterSlug } from "@/lib/shelterSlug";
import { buildShelterUpdateChoiceUrl } from "@/lib/shelterUpdateUrl";
import { updateLinkFields, shelterFields } from "@/lib/analyticsEvents";
import { isUrgentNeedsActive } from "@/types/shelter";
import { format } from "date-fns";
import { pl } from "date-fns/locale";

const SITE = "https://gdzieschronisko.pl";

const ShelterPage = () => {
  const { slug = "" } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: shelters, isLoading } = useShelters();

  const baseShelter = useMemo(() => {
    if (!shelters) return null;
    // 1) Najpierw spróbuj po slug-u (nowy format: "miasto-nazwa" lub "miasto-nazwa-2")
    const bySlug = shelters.find((s) => s.slug && s.slug === slug);
    if (bySlug) return bySlug;
    // 2) Fallback dla starych linków z 8-znakowym ID (np. "miasto-nazwa-a3f9b21c")
    const shortId = extractIdFromSlug(slug);
    if (!shortId) return null;
    return shelters.find((s) => matchesShortId(s.id, shortId)) ?? null;
  }, [shelters, slug]);

  const { data: details } = useShelterDetails(baseShelter?.id);

  const shelter = useMemo(() => {
    if (!baseShelter) return null;
    return {
      ...baseShelter,
      description: baseShelter.description ?? details?.description ?? null,
      volunteer_info: baseShelter.volunteer_info ?? details?.volunteer_info ?? null,
    };
  }, [baseShelter, details]);

  useEffect(() => {
    if (!shelter) return;
    const canonical = buildShelterSlug(shelter);
    if (canonical !== slug) {
      navigate(`/schronisko/${canonical}${window.location.search}${window.location.hash}`, { replace: true });
    }
  }, [shelter, slug, navigate]);

  // Przekierowanie ze starego slugu (po ręcznej regeneracji przez admina)
  const [redirectChecked, setRedirectChecked] = useState(false);
  useEffect(() => {
    if (isLoading || baseShelter || !slug) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("shelter_slug_redirects")
        .select("shelter_id, shelters(slug)")
        .eq("old_slug", slug)
        .maybeSingle();
      if (cancelled) return;
      const newSlug = (data as any)?.shelters?.slug;
      if (newSlug) {
        navigate(`/schronisko/${newSlug}${window.location.search}${window.location.hash}`, { replace: true });
      } else {
        setRedirectChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [isLoading, baseShelter, slug, navigate]);

  // Atrybucja: wejście z udostępnionego linku (utm_source=share)
  // Dedup w localStorage z oknem 24h – ogranicza zawyżenia z nowych kart / in-app browserów
  useEffect(() => {
    if (!shelter) return;
    if (typeof window === "undefined") return;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("utm_source") !== "share") return;
      const dedupKey = `shared_visit_${shelter.id}`;
      const WINDOW_MS = 24 * 60 * 60 * 1000;
      const now = Date.now();
      const prev = Number(localStorage.getItem(dedupKey) || 0);
      if (prev && now - prev < WINDOW_MS) return;
      localStorage.setItem(dedupKey, String(now));
      trackEvent("Profil - wejście z linku", {
        ...shelterFields(shelter),
        slug,
      });
    } catch {
      // ignore – tracking nie może blokować strony
    }
  }, [shelter, slug]);

  if (isLoading || (!baseShelter && !redirectChecked)) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-2/3" />
            <div className="h-4 bg-muted rounded w-1/3" />
            <div className="h-32 bg-muted rounded" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!shelter) {
    return (
      <div className="min-h-screen flex flex-col">
        <Helmet>
          <title>Nie znaleziono schroniska | GdzieSchronisko</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <Header />
        <main className="flex-1 container mx-auto px-4 py-16 text-center">
          <h1 className="font-display text-2xl font-bold mb-4">Nie znaleziono schroniska</h1>
          <p className="text-muted-foreground mb-6">Link mógł być błędny lub schronisko zostało usunięte z bazy.</p>
          <Button asChild><Link to="/">Wróć do mapy</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  const canonicalSlug = buildShelterSlug(shelter);
  const url = `${SITE}/schronisko/${canonicalSlug}`;
  // Unikamy "Schronisko Schronisko ..." gdy nazwa już zaczyna się od "Schronisko"
  const nameStartsWithSchronisko = /^schronisk/i.test(shelter.name.trim());
  const displayName = nameStartsWithSchronisko ? shelter.name : `Schronisko ${shelter.name}`;
  const baseTitle = `${displayName}, ${shelter.city}`;
  const suffix = " – schronisko";
  const title = baseTitle.length + suffix.length <= 60 ? baseTitle + suffix : baseTitle;
  const descParts = [
    `${displayName} w ${shelter.city}.`,
    shelter.current_needs ? `Aktualne potrzeby: ${shelter.current_needs.slice(0, 80)}.` : null,
    "Sprawdź dane kontaktowe, możliwości wolontariatu i adopcji.",
  ].filter(Boolean);
  const description = descParts.join(" ").slice(0, 158);

  // JSON-LD
  const addressLines = [shelter.address, shelter.postal_code, shelter.city].filter(Boolean).join(", ");
  const jsonLd: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "AnimalShelter",
    "@id": url,
    name: shelter.name,
    url,
    description: shelter.description || `Schronisko dla zwierząt w ${shelter.city}.`,
    address: {
      "@type": "PostalAddress",
      addressCountry: "PL",
      addressLocality: shelter.city,
      ...(shelter.region ? { addressRegion: shelter.region } : {}),
      ...(shelter.postal_code ? { postalCode: shelter.postal_code } : {}),
      ...(shelter.address ? { streetAddress: shelter.address } : {}),
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: shelter.lat,
      longitude: shelter.lng,
    },
    ...(shelter.phone ? { telephone: shelter.phone.split(";")[0].trim() } : {}),
    ...(shelter.email ? { email: shelter.email } : {}),
    ...(shelter.website ? { sameAs: [shelter.website] } : {}),
    areaServed: shelter.city,
    isAccessibleForFree: true,
  };

  const formattedDate = shelter.last_updated
    ? format(new Date(shelter.last_updated), "d MMMM yyyy", { locale: pl })
    : null;

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:locale" content="pl_PL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <main className="flex-1 container mx-auto px-3 sm:px-4 py-2 sm:py-6 max-w-5xl animate-fade-in">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-muted-foreground hover:text-primary transition-colors mb-3"
        >
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Wróć do mapy
        </Link>

        {/* HERO - glassmorphism w stylu HeroSection */}
        <header className="relative overflow-hidden rounded-2xl border border-white/50 bg-card/70 backdrop-blur-xl shadow-glow-soft px-4 py-4 sm:px-6 sm:py-6 mb-3 sm:mb-4 animate-fade-in">
          {/* Mesh + glow blobs jak w hero */}
          <div className="absolute inset-0 bg-hero-mesh opacity-70 pointer-events-none" aria-hidden />
          <div className="absolute -top-24 -left-24 w-[320px] h-[320px] rounded-full bg-primary/15 blur-3xl pointer-events-none" aria-hidden />
          <div className="absolute -bottom-28 -right-20 w-[340px] h-[340px] rounded-full bg-walking/10 blur-3xl pointer-events-none" aria-hidden />

          <div className="relative">
            {/* Przycisk udostępniania w prawym górnym rogu - gdy schronisko prowadzone przez organizację */}
            {shelter.has_owner && (
              <div className="absolute top-0 right-0 z-10 [&_button]:!h-auto [&_button]:!rounded-md [&_button]:!border [&_button]:!border-primary/25 [&_button]:!bg-card/80 [&_button]:!px-2.5 [&_button]:!py-1.5 [&_button]:!text-primary [&_button]:hover:!bg-primary/10 [&_button]:hover:!text-primary [&_button]:hover:!border-primary/40 [&_button]:!backdrop-blur-sm [&_svg]:!w-3.5 [&_svg]:!h-3.5">
                <ShareChoiceDialog shelter={shelter} variant="icon" />
              </div>
            )}

            {/* Pille statusu nad tytułem */}
            <div className={`flex flex-wrap items-center gap-1.5 mb-2 ${shelter.has_owner ? "pr-12" : ""}`}>
              {isUrgentNeedsActive(shelter) && (
                <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-urgent text-urgent-foreground shadow-sm">
                  <AlertTriangle className="w-3 h-3" />
                  Pilne potrzeby
                </span>
              )}
              {shelter.organization_verified && (
                <span
                  className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 rounded-full bg-primary/10 border border-primary/25 text-primary"
                  title={shelter.organization_verified_at ? `Data weryfikacji: ${format(new Date(shelter.organization_verified_at), "d MMMM yyyy", { locale: pl })}` : undefined}
                >
                  <BadgeCheck className="w-3 h-3" />
                  Zweryfikowane
                </span>
              )}
            </div>

            <h1 className="font-display font-extrabold text-lg sm:text-2xl leading-tight tracking-tight">
              <span className="bg-gradient-to-r from-primary via-primary to-walking bg-clip-text text-transparent">
                {shelter.name}
              </span>
            </h1>
            <p className="flex items-center gap-1.5 text-[12px] sm:text-sm text-muted-foreground mt-1">
              <MapPin className="w-3.5 h-3.5 text-primary/70" />
              {shelter.city}{shelter.region && `, ${shelter.region}`}
            </p>

            {!shelter.has_owner && (
              <div className="flex flex-row flex-wrap items-stretch gap-1.5 sm:gap-2 mt-3">
                <Link
                  to={buildShelterUpdateChoiceUrl(shelter, "needs")}
                  onClick={() => trackEvent("Zaktualizuj potrzeby", updateLinkFields(shelter, "needs", "shelter_page"))}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-card/80 text-primary border border-primary/25 rounded-md text-[11px] sm:text-xs font-semibold hover:bg-primary/10 hover:border-primary/40 transition-all backdrop-blur-sm"
                >
                  <Heart className="w-3 h-3 flex-shrink-0" />
                  <span>Zaktualizuj potrzeby</span>
                </Link>
                <Link
                  to={buildShelterUpdateChoiceUrl(shelter, "info")}
                  onClick={() => trackEvent("Zaktualizuj dane", updateLinkFields(shelter, "info", "shelter_page"))}
                  className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 bg-card/80 text-primary border border-primary/25 rounded-md text-[11px] sm:text-xs font-semibold hover:bg-primary/10 hover:border-primary/40 transition-all backdrop-blur-sm"
                >
                  <FileEdit className="w-3 h-3 flex-shrink-0" />
                  <span>Zaktualizuj dane</span>
                </Link>
                <div className="flex-shrink-0 flex items-stretch [&_button]:!h-auto [&_button]:!rounded-md [&_button]:!border [&_button]:!border-primary/25 [&_button]:!bg-card/80 [&_button]:!px-2.5 [&_button]:!py-1.5 [&_button]:!text-primary [&_button]:hover:!bg-primary/10 [&_button]:hover:!text-primary [&_button]:hover:!border-primary/40 [&_button]:!backdrop-blur-sm [&_svg]:!w-3 [&_svg]:!h-3">
                  <ShareChoiceDialog shelter={shelter} variant="icon" />
                </div>
              </div>
            )}
          </div>
        </header>

        {shelter.has_owner && (
          <div className="mb-3 sm:mb-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-primary text-[11px] sm:text-xs font-medium">
            <BadgeCheck className="w-3 h-3 flex-shrink-0" />
            Profil prowadzony przez organizację
          </div>
        )}

        {!shelter.has_owner && (
          <div className="mb-3 sm:mb-4 rounded-lg border border-amber-300/50 bg-amber-50/70 dark:bg-amber-950/20 px-2.5 py-1.5 flex items-center gap-2 animate-fade-in [animation-delay:80ms] [animation-fill-mode:backwards]">
            <Info className="w-3.5 h-3.5 text-amber-700 dark:text-amber-400 flex-shrink-0" />
            <p className="text-[11px] sm:text-xs text-amber-900 dark:text-amber-200 leading-snug">
              <span className="font-semibold">Schronisko niezweryfikowane.</span>{" "}
              Dane mogą być nieaktualne - potwierdź je bezpośrednio ze schroniskiem.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-4">
          {/* MAIN COLUMN */}
          <div className="lg:col-span-2 space-y-2 sm:space-y-4">
            {shelter.description && (
              <section className="relative bg-card/70 backdrop-blur-xl border border-white/50 rounded-2xl p-3 sm:p-4 shadow-glow-soft animate-fade-in [animation-delay:120ms] [animation-fill-mode:backwards]">
                <h2 className="font-display font-semibold text-sm mb-2 text-foreground">O schronisku</h2>
                <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">{shelter.description}</p>
              </section>
            )}

            {shelter.current_needs && (
              <section className={`relative rounded-2xl p-3 sm:p-4 backdrop-blur-xl border shadow-glow-soft animate-fade-in [animation-delay:180ms] [animation-fill-mode:backwards] overflow-hidden ${isUrgentNeedsActive(shelter) ? 'bg-urgent/10 border-urgent/30' : 'bg-card/70 border-white/50'}`}>
                {isUrgentNeedsActive(shelter) && (
                  <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-urgent/15 blur-2xl pointer-events-none" aria-hidden />
                )}
                <h2 className="relative font-display font-semibold text-sm mb-1.5 sm:mb-2.5 flex items-center gap-2 text-foreground">
                  <span className={`flex items-center justify-center w-7 h-7 rounded-xl ring-1 ${isUrgentNeedsActive(shelter) ? 'bg-urgent text-urgent-foreground ring-urgent/30' : 'bg-primary/15 text-primary ring-primary/20'}`}>
                    <AlertTriangle className="w-3.5 h-3.5" />
                  </span>
                  Obecne potrzeby
                </h2>
                <p className="relative text-card-foreground text-xs sm:text-sm leading-relaxed whitespace-pre-line break-words [overflow-wrap:anywhere]">{shelter.current_needs}</p>
              </section>
            )}

            <section className="relative bg-card/70 backdrop-blur-xl border border-white/50 rounded-2xl p-3 sm:p-4 shadow-glow-soft animate-fade-in [animation-delay:240ms] [animation-fill-mode:backwards]">
              <h2 className="font-display font-semibold text-sm mb-1.5 sm:mb-2.5 flex items-center gap-2 text-foreground">
                <span className="flex items-center justify-center w-7 h-7 rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
                  <Users className="w-3.5 h-3.5" />
                </span>
                Wolontariat i spacery
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-muted/50 border border-border/40 text-xs sm:text-sm">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${shelter.dog_walking_available === true ? 'bg-walking' : shelter.dog_walking_available === false ? 'bg-orange-300' : 'bg-muted-foreground/30'}`} />
                  <span className="text-muted-foreground">Spacery z psami:</span>
                  <span className="font-medium text-foreground">{shelter.dog_walking_available === true ? 'możliwe' : shelter.dog_walking_available === false ? 'niedostępne' : 'brak informacji'}</span>
                </div>
                <div className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-muted/50 border border-border/40 text-xs sm:text-sm">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${shelter.volunteering_available === true ? 'bg-teal-500' : shelter.volunteering_available === false ? 'bg-orange-300' : 'bg-muted-foreground/30'}`} />
                  <span className="text-muted-foreground">Wolontariat:</span>
                  <span className="font-medium text-foreground">{shelter.volunteering_available === true ? 'możliwy' : shelter.volunteering_available === false ? 'niedostępny' : 'brak informacji'}</span>
                </div>
              </div>
              {shelter.volunteer_info && (
                <p className="text-sm text-muted-foreground leading-relaxed mt-2.5 pt-2.5 border-t border-border/60 whitespace-pre-line break-words [overflow-wrap:anywhere]">{shelter.volunteer_info}</p>
              )}
            </section>

            <section className="relative bg-card/70 backdrop-blur-xl border border-white/50 rounded-2xl p-3 sm:p-4 shadow-glow-soft animate-fade-in [animation-delay:300ms] [animation-fill-mode:backwards]">
              <h2 className="font-display font-semibold text-sm mb-1.5 sm:mb-2.5 flex items-center gap-2 text-foreground">
                <span className="flex items-center justify-center w-7 h-7 rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
                  <Heart className="w-3.5 h-3.5" />
                </span>
                Adopcja
              </h2>
              <div className="grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-muted/50 border border-border/40 text-xs sm:text-sm">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${shelter.dogs_available === true ? 'bg-blue-500' : shelter.dogs_available === false ? 'bg-orange-300' : 'bg-muted-foreground/30'}`} />
                  <Dog className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">Psy:</span>
                  <span className="font-medium text-foreground">{shelter.dogs_available === true ? 'tak' : shelter.dogs_available === false ? 'nie' : '-'}</span>
                </div>
                <div className="flex items-center gap-2 px-2.5 py-2 rounded-xl bg-muted/50 border border-border/40 text-xs sm:text-sm">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${shelter.cats_available === true ? 'bg-purple-500' : shelter.cats_available === false ? 'bg-orange-300' : 'bg-muted-foreground/30'}`} />
                  <Cat className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">Koty:</span>
                  <span className="font-medium text-foreground">{shelter.cats_available === true ? 'tak' : shelter.cats_available === false ? 'nie' : '-'}</span>
                </div>
              </div>
            </section>
          </div>

          {/* SIDEBAR */}
          <aside className="space-y-2 sm:space-y-4 lg:sticky lg:top-4 lg:self-start animate-fade-in [animation-delay:360ms] [animation-fill-mode:backwards]">
            <section className="relative bg-card/70 backdrop-blur-xl border border-white/50 rounded-2xl p-3 sm:p-4 shadow-glow-soft">
              <h2 className="font-display font-semibold text-sm mb-2 text-foreground">Dane kontaktowe</h2>
              <div className="flex flex-col">
                {addressLines && (
                  <div className="flex items-center gap-2.5 p-2 -mx-2 text-xs sm:text-sm">
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary flex-shrink-0">
                      <MapPin className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-foreground break-words leading-snug">{addressLines}</span>
                  </div>
                )}
                {shelter.phone && (
                  <div className="flex items-center gap-1">
                    <a href={`tel:${shelter.phone.split(';')[0].trim()}`} className="group flex items-center gap-2.5 p-2 -mx-2 rounded-lg hover:bg-muted/60 transition-colors text-xs sm:text-sm flex-1 min-w-0">
                      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Phone className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-foreground break-all tabular-nums leading-snug">{shelter.phone}</span>
                    </a>
                    <CopyButton value={shelter.phone} label="Telefon" />
                  </div>
                )}
                {shelter.email && (
                  <div className="flex items-center gap-1">
                    <a href={`mailto:${shelter.email}`} className="group flex items-center gap-2.5 p-2 -mx-2 rounded-lg hover:bg-muted/60 transition-colors text-xs sm:text-sm flex-1 min-w-0">
                      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Mail className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-foreground break-all leading-snug">{shelter.email}</span>
                    </a>
                    <CopyButton value={shelter.email} label="Email" />
                  </div>
                )}
                {shelter.website && (
                  <div className="flex items-center gap-1">
                    <a href={shelter.website} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-2.5 p-2 -mx-2 rounded-lg hover:bg-muted/60 transition-colors text-xs sm:text-sm flex-1 min-w-0">
                      <span className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 text-primary flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <Globe className="w-3.5 h-3.5" />
                      </span>
                      <span className="text-foreground break-all leading-snug">{shelter.website.replace(/^https?:\/\//, '')}</span>
                    </a>
                    <CopyButton value={shelter.website} label="Strona WWW" />
                  </div>
                )}
              </div>
            </section>
            {formattedDate && (
              <p className="flex items-center gap-2 text-xs text-muted-foreground px-1">
                <Clock className="w-3.5 h-3.5" />
                Ostatnia aktualizacja: {formattedDate}
              </p>
            )}
            {!shelter.has_owner && (
              <Link
                to={`/uzyskaj-dostep/${shelter.id}`}
                onClick={() => trackEvent("Przejmij profil - CTA", { ...shelterFields(shelter) })}
                className="text-xs text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1 px-1"
              >
                Uzyskaj dostęp do profilu schroniska
                <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ShelterPage;
